"use client";

/**
 * Renders grid of asset thumbnails for a section in a kit, with inline notes.
 */

import React, {
  useRef,
  useState,
  useCallback,
  useMemo,
  useEffect,
  DragEvent,
  MutableRefObject,
} from "react";
import { usePathname, useSearchParams } from "next/navigation";
import _isEqual from "lodash/isEqual";
import _cloneDeep from "lodash/cloneDeep";

import {
  ItemType,
  Box,
  useContextMenu,
  AnyObject,
  Item,
  Section,
  useNavigation,
  EmptyState,
  usePaste,
} from "@thenounproject/lingo-core";

// Actions
import useNotifications from "@actions/useNotifications";
import useShowModal, { ModalTypes } from "@redux/actions/useModals";

import GalleryDropZone from "./GalleryDropZone";
import { getVersionedItemId, findItemIndexByVersionedId } from "@helpers/items";
import useKeyboardHandler from "./useKeyboardHandler";
import stripVersion from "@helpers/stripVersion";
import useResizeObserver from "../hooks/useResizeObserver";

import { hasContextMenu } from "@src/features/context-menus/hasContextMenu";
import ActionsMenu from "@src/features/context-menus/ActionMenu/ActionsMenu";
import * as galleryUtils from "@helpers/gallery";
import { insertData as insertDataMap, InsertType } from "../../constants/InsertType";
import { isValidSelfDropPosition } from "@helpers/layoutValidation";
import * as spacingUtils from "@helpers/galleryItemSpacing";
import InsertAssetMenu from "./InsertAssetMenu";
import { Inspectable } from "@constants/Inspector";
import { useAppSelectorV1 } from "@redux/hooks";
import { useSelectionActions } from "@contexts/SelectedItemsProvider";
import type { RootState } from "@redux/store";
import useBatchSaveItems from "@redux/actions/items/useBatchSaveItems";
import useBatchUpdateItemStatus from "@actions/items/useBatchUpdateItemStatus";
import useUpdateItem from "@redux/actions/items/useUpdateItem";
import { useSelectSpace } from "@redux/selectors/entities/spaces";
import { InsertPosition } from "@redux/actions/uploads";
import useCreateFileAssets from "@redux/actions/items/useCreateFileAssets";
import { DropInfo, GalleryItemsLayout } from "./GalleryItemsLayout";
import { v4 as genUuid } from "uuid";
import useReorderSectionItems from "@actions/items/useReorderSectionItems";
import useDuplicateItems from "@redux/actions/items/useDuplicateItems";
import SelectableCanvasItem from "./canvas/SelectableCanvasItem";
import { AssetExportController, ExportContext } from "@features/asset-export";
import { getVersionedIdentifier } from "@src/@redux/utils/identifiers";
import useCreateAssetsFromSource from "@actions/items/useCreateAssetsFromSource";
import { canSelectItem } from "@src/helpers/selection";
import { useAssetPaste } from "@features/asset-paste";
import { getFilesFromDataTransfer } from "@helpers/getFilesFromDataTransfer";

const dragImageStyleOverride = `
  height: 32px;
  width: 32px;
  border-radius: 100px;
  background: #cf4a4a;
  position: absolute;
  color: white;
  font-size: 16px;
  font-weight: bold;
  display: flex;
  justify-content: center;
  align-items: center;
  font-family: Inter;
  top: -1000px;
`;

type Props = {
  section?: Section;
  items: Item[];
  item?: Item;
  getItemUrl: (item: Item) => string;
  onSelectItem: (event: React.MouseEvent | KeyboardEvent, itemId: string, items: Item[]) => void;
  setSelectedItems: (itemIds: string[]) => void;
  selectedItems: string[];
  canEditContent: boolean;
  scrollContainerRef: MutableRefObject<HTMLDivElement>;
  updateItem?: ReturnType<typeof useUpdateItem>[0];
  batchUpdateItemStatus?: ReturnType<typeof useBatchUpdateItemStatus>[0];
};

export const GalleryItems: React.FC<Props> = ({
  section,
  items,
  item,
  getItemUrl,
  onSelectItem,
  selectedItems,
  setSelectedItems,
  scrollContainerRef,
  updateItem,
  batchUpdateItemStatus,
  canEditContent,
}) => {
  const navigation = useNavigation();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const space = useSelectSpace();
  const itemWrapperRef = useRef(null);
  const [editingItem, setEditingItem] = useState(null);
  const [assetMenuData, setAssetMenuData] = useState<{
    insertPosition: InsertPosition;
    coordinates: { xPos: number | string; yPos: number | string };
  } | null>(null);
  const [exportContext, setExportContext] = useState<ExportContext | null>(null);

  // Use direct store access for selection actions (no re-render on selection change)
  const {
    setSelectedItems: selectItems,
    getSelection,
    isSelected: checkIsSelected,
    registerEditHandler: registerEditHandlerInStore,
    unregisterEditHandler,
  } = useSelectionActions();

  const [createFileAssets] = useCreateFileAssets(),
    [createAssetsFromSource] = useCreateAssetsFromSource(),
    { showNotification } = useNotifications(),
    { showModal } = useShowModal();

  const [reorderSectionItems] = useReorderSectionItems();
  const [duplicateItems] = useDuplicateItems();

  const galleryOutline = useMemo(() => galleryUtils.getGalleryOutline(items), [items]);

  const isModalOpen = useAppSelectorV1((state: RootState) => Boolean(state.modals.length));
  const [batchSaveItems] = useBatchSaveItems();
  const [containerWidth, setContainerWidth] = useState<number>(null);

  const onContainerResize = useCallback((node: HTMLDivElement) => {
    setContainerWidth(node.clientWidth);
  }, []);

  useResizeObserver(scrollContainerRef, onContainerResize);

  const inspectables: Inspectable[] = useMemo(() => {
    return selectedItems.reduce((acc, item) => {
      const id = item.substring(0, item.lastIndexOf("-"));
      const stateItem = items.find(i => i.id === id);
      if (stateItem) {
        acc.push({ item: stateItem, asset: stateItem.asset });
      }
      return acc;
    }, []);
  }, [items, selectedItems]);

  // Store the latest inspectables in a ref to avoid recreating callbacks
  const inspectablesRef = useRef(inspectables);
  inspectablesRef.current = inspectables;

  const handleRouteToAsset = useCallback(
    (item: Item) => {
      return navigation.push(getItemUrl(item));
    },
    [getItemUrl, navigation]
  );

  const onTogglePreview = useCallback(
    (item: Item) => {
      if (!item) return;
      handleRouteToAsset(item);
    },
    [handleRouteToAsset]
  );

  const openInsertAssetMenu = useCallback((e: MouseEvent, insertPosition: InsertPosition) => {
    setAssetMenuData({
      insertPosition,
      coordinates: { xPos: `${e.pageX}px`, yPos: `${e.pageY}px` },
    });
  }, []);

  const closeInsertAssetMenu = useCallback(() => {
    setAssetMenuData(null);
  }, []);

  const selectedItem = selectedItems.length ? selectedItems[selectedItems.length - 1] : null,
    selectedIndex = selectedItem ? findItemIndexByVersionedId(selectedItem, items) : null;

  useKeyboardHandler({
    items: items,
    canEdit: canEditContent,
    selectedIndex: selectedIndex,
    onSelectItem: onSelectItem,
    onTogglePreview: onTogglePreview,
  });

  function renderEmptySection() {
    if (items.length) return null;
    if (!section) return null;
    return canEditContent ? (
      <EmptyState
        title="This page is empty"
        subtitle="Drag &amp; Drop from the Insert Panel to add content.."
        iconProps={{
          iconId: "empty-state.assets",
          size: "90",
          fill: "gray",
        }}
        dropZone={
          <GalleryDropZone
            sectionId={section.id}
            kitId={section.kitId}
            startEditingItem={_onStartEditing}
            openInsertAssetMenu={openInsertAssetMenu}
            catchAll
          />
        }
      />
    ) : (
      <EmptyState
        title="This page is empty"
        subtitle="You don't have permission to create anything."
        iconProps={{ iconId: "info.lock-open", size: "90", fill: "gray" }}
      />
    );
  }

  const _onStartEditing = useCallback(
    (itemId: string) => {
      const _itemId = getVersionedIdentifier(itemId, 0);
      setEditingItem(itemId);
      if (!checkIsSelected("kitGallery", _itemId)) {
        onSelectItem(null, _itemId, []);
      }
    },
    [checkIsSelected, onSelectItem]
  );

  // Register the edit handler with selection store (for Inspector to use)
  useEffect(() => {
    registerEditHandlerInStore("kitGallery", _onStartEditing);
    return () => {
      unregisterEditHandler("kitGallery");
    };
  }, [registerEditHandlerInStore, unregisterEditHandler, _onStartEditing]);

  const _onFinishEditing = useCallback(
    async (item: Item, data: AnyObject) => {
      if (!updateItem) return;
      if (!data) {
        return setEditingItem(null);
      }
      const deleteIfEmpty = [ItemType.codeSnippet, ItemType.note, ItemType.heading];
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (deleteIfEmpty.includes(item.type) && data.data?.content === "") {
        // If the text content is removed, delete the item
        const { response, error: responseError } = await batchUpdateItemStatus({
          items: [item.id],
          status: "trashed",
        });

        if (responseError) {
          showNotification({
            message: responseError.message ?? "Failed to delete items.",
            level: "error",
          });
        } else {
          const { result, entities } = response;
          const [itemResult] = result.items;

          if (itemResult.success) {
            const deletedItem = entities.items[itemResult.result];
            const currentHash = typeof window !== "undefined" ? window.location.hash : "";
            const deletedCurrentHash = currentHash
              ? deletedItem.shortId === currentHash.slice(1)
              : false;
            if (deletedCurrentHash) {
              const searchString = searchParams.toString();
              const newUrl = `${pathname}${searchString ? `?${searchString}` : ""}`;
              navigation.replace(newUrl);
            }
            setSelectedItems([]);
          } else {
            showNotification({ message: itemResult.error.message, level: "error" });
          }
        }
      } else {
        const { error: responseError } = await updateItem({ itemId: item.id, updates: data });
        if (responseError)
          showNotification({
            message: responseError.message ?? "Failed to save item content.",
            level: "error",
          });
        else {
          setEditingItem(null);
        }
      }
    },
    [
      updateItem,
      batchUpdateItemStatus,
      showNotification,
      pathname,
      searchParams,
      navigation,
      setSelectedItems,
    ]
  );

  // Paste handling for colors, Figma links, and images
  const { pasteHandler } = useAssetPaste({
    insertConfig: {
      spaceId: space?.id,
      kitId: section?.kitId,
      sectionId: section?.id,
      items,
      selectedItems,
    },
  });

  usePaste(pasteHandler, isModalOpen || !canEditContent || !section?.id || !items);

  const handleGridDrop = useCallback(
    async (e: DragEvent, dropInfo: DropInfo) => {
      e.preventDefault();
      e.stopPropagation();

      const { insertIndex, displayOrder } = dropInfo;

      // Helper to normalize displayOrder
      const normalizeDisplayOrder = (order: string): string => {
        if (order === "prepend" && items.length > 0) {
          return `before:${items[0].id}`;
        }
        if (order === "append" && items.length > 0) {
          return `after:${items[items.length - 1].id}`;
        }
        return order;
      };

      // Helper to create insert position
      const createInsertPosition = (order: string) => ({
        displayOrder: normalizeDisplayOrder(order),
        insertIndex,
        kitId: section?.kitId,
        sectionId: section?.id,
        spaceId: space?.id,
        nextItemId: items[insertIndex]?.id,
      });

      // === DUPLICATE ITEMS VIA OPTION+DRAG ===
      // Option only: deep duplicate (new independent assets from same file)
      // Option + Meta: shallow duplicate (references same asset)
      const duplicateMode = e.altKey;
      const reorderItemIds = e.dataTransfer?.getData("reorderItemIds");

      if (duplicateMode && reorderItemIds && section) {
        const versionedItemIds = reorderItemIds.split(",").filter(id => id.trim());
        if (versionedItemIds.length === 0) return;

        const itemIds = versionedItemIds.map(versionedId => stripVersion(versionedId));
        const position = normalizeDisplayOrder(displayOrder);

        const { error } = await duplicateItems({
          itemIds,
          toSectionId: section.id,
          createReferences: Boolean(e.metaKey),
          position,
          insertIndex,
        });

        if (error) {
          showNotification({
            message: error.message ?? "Failed to duplicate items",
            level: "error",
          });
        }
        return;
      }

      // === REORDER EXISTING ITEMS ===
      if (reorderItemIds && section) {
        const itemIds = reorderItemIds.split(",").filter(id => id.trim());

        if (itemIds.length === 0) {
          return;
        }

        // Check for self-drop
        const firstDraggedItem = items.find(
          item => itemIds.includes(`${item.id}-0`) || itemIds.includes(item.id)
        );
        if (firstDraggedItem) {
          const currentIndex = items.indexOf(firstDraggedItem);
          const isSelfDrop = currentIndex === insertIndex || currentIndex === insertIndex - 1;

          if (isSelfDrop) {
            const isFullGuide =
              firstDraggedItem.type === ItemType.guide && firstDraggedItem.data.displaySize < 2;
            const isDraggedItemGuide = firstDraggedItem.type === ItemType.guide;
            const validSelfDropPosition =
              isDraggedItemGuide &&
              dropInfo.guidePosition &&
              dropInfo.dragPosition &&
              isValidSelfDropPosition(dropInfo.dragPosition, dropInfo.guidePosition);
            const shouldAllowDrop =
              (isFullGuide && dropInfo.type === "between-items") || validSelfDropPosition;
            if (!shouldAllowDrop) {
              return;
            }
          }
        }

        const { error: reorderError } = await reorderSectionItems({
          reorderItemIds: itemIds,
          displayOrder: normalizeDisplayOrder(displayOrder),
          sectionId: section?.id || e.dataTransfer?.getData("fromSectionId"),
        });

        if (reorderError) {
          showNotification({
            message: reorderError.message || "Failed to reorder items",
            level: "error",
          });
        }
        return;
      }

      // === FILE DROPS ===
      const hasFiles = e.dataTransfer?.types?.includes("Files");
      if (hasFiles && e.dataTransfer?.items?.length > 0) {
        const files = await getFilesFromDataTransfer(e.dataTransfer);
        const insertPosition = createInsertPosition(displayOrder);

        const { error: fileError } = await createFileAssets({
          files,
          itemType: ItemType.asset,
          insertPosition,
        });

        if (fileError) {
          showNotification({
            message: fileError.message || "Failed to add files",
            level: "error",
          });
        }
        return;
      }

      // === INSERT NEW ITEMS FROM PANEL ===
      const insertType = e.dataTransfer?.getData("insertType") as InsertType;
      if (!insertType) {
        return;
      }

      const insertData = insertDataMap[
        insertType
      ] as (typeof insertDataMap)[keyof typeof insertDataMap];

      const insertPosition = createInsertPosition(displayOrder);
      // Asset insert - open menu for file picker
      if (insertType === InsertType.assets) {
        openInsertAssetMenu(e as unknown as MouseEvent, insertPosition);
        return;
      }

      // Supporting content - open file picker modal
      if (insertType === InsertType.supportContent) {
        showModal(ModalTypes.PICK_FILE, {
          onUploadFiles: (files: File[]) =>
            createFileAssets({
              files,
              itemType: ItemType.asset,
              insertPosition,
              displayProperties: { size: 1, allowDownload: false, showMetadata: false },
              previewBackground: "clear",
            }),
          onImportFiles(files) {
            return createAssetsFromSource({
              sources: files,
              insertPosition,
              itemType: ItemType.asset,
              displayProperties: { size: 1, allowDownload: false, showMetadata: false },
              previewBackground: "clear",
            });
          },
          itemType: ItemType.asset,
          multiple: false,
          insertPosition,
        });
        return;
      }

      if (insertType === InsertType.gallery) {
        // If it's a gallery, open the gallery creation modal
        showModal(ModalTypes.CREATE_GALLERY_ITEM, {
          insertPosition,
        });
        return;
      }

      // Other items (heading, note, code, guide, etc.) - create directly
      const newItem = {
        uuid: genUuid().toUpperCase(),
        kit_uuid: section?.kitId,
        section_uuid: section?.id,
        display_order: normalizeDisplayOrder(displayOrder),
        type: insertData.type || ItemType.asset,
        ...insertData,
      };

      const { response, error: batchError } = await batchSaveItems({
        updatedItems: [],
        insertConfig: {
          sectionId: section?.id,
          newItems: [newItem],
          insertIndex,
          insertType,
          guidePosition: dropInfo.guidePosition,
          dragPosition: dropInfo.dragPosition,
          targetIndex: dropInfo.targetItem ? items.indexOf(dropInfo.targetItem) : undefined,
        },
      });

      if (batchError) {
        showNotification({
          message: batchError.message || "Failed to add item",
          level: "error",
        });
      } else if (response?.newItems?.[0]) {
        const item = response.newItems[0];
        const editableTypes = [ItemType.heading, ItemType.note, ItemType.codeSnippet];

        if (editableTypes.includes(item.type) && _onStartEditing) {
          _onStartEditing(item.uuid);
        }
      }
    },
    [
      section,
      batchSaveItems,
      items,
      space?.id,
      reorderSectionItems,
      duplicateItems,
      showNotification,
      createFileAssets,
      openInsertAssetMenu,
      showModal,
      createAssetsFromSource,
      _onStartEditing,
    ]
  );

  function renderActionsMenu() {
    if (!inspectables.length) return;
    if (!contextMenuOpen && !overflowMenuSource) return null;
    return (
      <ActionsMenu
        open={true}
        position={overflowMenuSource ? undefined : { x: contextMenuXPos, y: contextMenuYPos }}
        close={overflowMenuSource ? closeOverflowMenu : handleContextMenuClose}
        inspectables={inspectables}
        hPos={overflowMenuSource ? "alignLeft" : "floatRight"}
        vPos={overflowMenuSource ? "alignTop" : "floatBelow"}
        source={overflowMenuSource}
      />
    );
  }

  function renderInsertAssetMenu() {
    if (!assetMenuData) return null;
    return (
      <InsertAssetMenu
        close={closeInsertAssetMenu}
        coordinates={assetMenuData.coordinates}
        insertPosition={assetMenuData.insertPosition}
        startEditingItem={_onStartEditing}
      />
    );
  }

  const handleReorderDragEnd = useCallback((e: DragEvent) => {
    e.preventDefault();
  }, []);

  const handleReorderDragStart = useCallback(
    (e: DragEvent<HTMLElement>) => {
      // Disable drag/drop in gallery views (when section is null)
      if (!section) {
        e.preventDefault();
        return;
      }

      // ID of item upon which reorder drag is initiated
      e.dataTransfer.effectAllowed = "all";
      const versionedDragItemId = e.currentTarget.dataset.dragid;
      if (!versionedDragItemId) {
        e.preventDefault();
        console.warn("Tried to start a reorder, but the drag event couldn't parse an item ID.");
        return;
      }

      // Get current selection directly from store (always fresh)
      const currentSelection = getSelection("kitGallery");
      let itemsToReorder = currentSelection;

      // If the dragged item is not in the selection, start new selection with just this item
      if (!checkIsSelected("kitGallery", versionedDragItemId)) {
        itemsToReorder = [versionedDragItemId];
        setSelectedItems(itemsToReorder);
      }

      // Manipulate the drag image to indicate the length of the group of items
      if (itemsToReorder.length > 1) {
        const elem: HTMLElement = document.createElement("div");
        const style = dragImageStyleOverride;
        elem.setAttribute("style", style);
        elem.innerHTML = String(itemsToReorder.length);
        document.body.appendChild(elem);
        e.dataTransfer.setDragImage(elem, 0, 0);
        setTimeout(() => {
          document.body.removeChild(elem);
        }, 0);
      }
      // Add current selection to data transfer
      e.dataTransfer.setData("reorderItemIds", String(itemsToReorder));
      e.dataTransfer.setData("fromSectionId", String(section?.id));
    },
    [section, getSelection, checkIsSelected, setSelectedItems]
  );

  const onItemClicked = useCallback(
    (item: Item, e: React.MouseEvent) => {
      if (!canSelectItem(item, canEditContent)) {
        return setSelectedItems([]);
      }
      return onSelectItem(e, getVersionedItemId(item), items);
    },
    [canEditContent, items, onSelectItem, setSelectedItems]
  );

  const onItemDoubleClicked = useCallback(
    (item: Item, _e: React.MouseEvent) => {
      switch (item.type) {
        case ItemType.asset:
        case ItemType.gallery:
          return handleRouteToAsset(item);
        default:
          return;
      }
    },
    [handleRouteToAsset]
  );

  // MARK : Overflow menu
  // -------------------------------------------------------------------------------
  // Right click action menu
  const [deselectOnOverflowClose, setDeselectOnOverflowClose] = useState<boolean>(false);

  const overflowWillOpenWithItem = useCallback(
    (item: Item) => {
      setDeselectOnOverflowClose(!canSelectItem(item, canEditContent));
    },
    [canEditContent]
  );

  const { contextMenuXPos, contextMenuYPos, contextMenuOpen, handleContextMenuClose } =
    useContextMenu({
      itemRef: itemWrapperRef,
      onCloseMenu: useCallback(() => {
        if (deselectOnOverflowClose) {
          setSelectedItems([]);
        }
      }, [deselectOnOverflowClose, setSelectedItems]),
      onOpenMenu: useCallback(
        event => {
          let current = event.target as HTMLElement;
          let itemId: string;
          while (current && !itemId) {
            itemId = current.dataset.itemid;
            current = current.parentElement;
          }

          if (!itemId || !hasContextMenu(inspectablesRef.current, item)) return false;
          const isSelected = checkIsSelected("kitGallery", itemId);

          if (!isSelected) {
            onSelectItem(event, itemId, items);
          }
          if (!canEditContent) {
            const targetItem = items.find(i => getVersionedItemId(i) === itemId);
            if (!item) return; // should not happen
            overflowWillOpenWithItem(targetItem);
          }
          return true;
        },
        [canEditContent, checkIsSelected, item, items, onSelectItem, overflowWillOpenWithItem]
      ),
    });

  // Overflow button
  const [overflowMenuSource, setOverflowMenuSource] = useState<string>("");
  const closeOverflowMenu = useCallback(() => {
    // filter out non-selectable items
    if (deselectOnOverflowClose) {
      setSelectedItems([]);
    }
    setOverflowMenuSource(null);
  }, [deselectOnOverflowClose, setSelectedItems]);
  // onOverflowSelected
  const onOverflowSelected = useCallback(
    (item: Item) => {
      const versionedId = getVersionedItemId(item);
      if (!checkIsSelected("kitGallery", versionedId)) {
        selectItems({ type: "kitGallery", ids: [versionedId] });
      }
      overflowWillOpenWithItem(item);
      setOverflowMenuSource(`overflow-menu-${item.id}`);
    },
    [checkIsSelected, overflowWillOpenWithItem, selectItems]
  );

  // MARK : Use Menu / Asset Export
  // -------------------------------------------------------------------------------
  const closeExportController = useCallback(() => {
    setExportContext(null);
  }, []);

  const onUseButtonSelected = useCallback(
    (item: Item, e: React.MouseEvent) => {
      if (e) e.stopPropagation();

      const versionedId = getVersionedItemId(item);
      const targetItemInSelection = checkIsSelected("kitGallery", versionedId);

      if (!targetItemInSelection) {
        selectItems({ type: "kitGallery", ids: [versionedId] });
      }

      const currentInspectables = inspectablesRef.current;
      const targetInspectable = { item, asset: item.asset };
      const selectedInspectablesForExport = targetItemInSelection
        ? currentInspectables
        : [targetInspectable];

      setExportContext({
        targetInspectable,
        selectedInspectables: selectedInspectablesForExport,
        coordinates: { x: e.clientX, y: e.clientY },
        canEdit: canEditContent,
      });
    },
    [canEditContent, checkIsSelected, selectItems]
  );

  /**
   * Render function for the gallery grid.
   * Uses SelectableCanvasItem which subscribes to its own selection state,
   * preventing re-renders of all items when selection changes.
   */
  const renderItemForGrid = useCallback(
    (item: Item, globalIndex: number) => {
      const prevItem = globalIndex > 0 ? items[globalIndex - 1] : null;
      const secondPreviousItem = globalIndex > 1 ? items[globalIndex - 2] : null;

      // Compute spacing based on item type
      let spacingProps = {};
      let guidePosition: "full" | "left" | "right" = "full";

      switch (item.type) {
        case ItemType.asset: {
          const isFullWidthAsset =
            item?.displayProperties?.imageAlignment !== "top" ||
            item?.displayProperties?.size === 1;
          const prevAssetIsFullWidth =
            prevItem?.displayProperties?.imageAlignment !== "top" ||
            prevItem?.displayProperties?.size === 1;

          spacingProps = spacingUtils.getGalleryAssetSpacing({
            isFullWidthAsset,
            prevAssetIsFullWidth,
            isFirstItem: globalIndex === 0,
          });
          break;
        }
        case ItemType.supportingImage:
          spacingProps = spacingUtils.getGallerySupportSpacing(prevItem?.type, globalIndex);
          break;
        case ItemType.heading:
          spacingProps = spacingUtils.getGalleryHeadingSpacing(globalIndex);
          break;
        case ItemType.guide:
          guidePosition = galleryOutline[item.id] ? galleryOutline[item.id].position : "full";
          spacingProps = spacingUtils.getGalleryGuideSpacing(
            prevItem?.type,
            secondPreviousItem?.type,
            guidePosition,
            globalIndex
          );
          break;
        case ItemType.codeSnippet:
          spacingProps = spacingUtils.getGalleryCodeSpacing(prevItem?.type, globalIndex);
          break;
        case ItemType.note:
          spacingProps = spacingUtils.getGalleryNoteSpacing(prevItem?.type, globalIndex);
          break;
        case ItemType.gallery:
          spacingProps = spacingUtils.getGalleryItemSpacing(prevItem?.type, globalIndex);
          break;
      }

      return (
        <SelectableCanvasItem
          key={item.id}
          item={item}
          globalIndex={globalIndex}
          items={items}
          galleryOutline={galleryOutline}
          editingItem={editingItem}
          canEditContent={canEditContent}
          canGenerateText={canEditContent && space?.features.includes("generative_text")}
          onOverflowSelected={onOverflowSelected}
          onItemDoubleClicked={onItemDoubleClicked}
          onItemClicked={onItemClicked}
          onUseButtonSelected={onUseButtonSelected}
          handleReorderDragEnd={handleReorderDragEnd}
          handleReorderDragStart={handleReorderDragStart}
          onStartEditing={_onStartEditing}
          onFinishEditing={_onFinishEditing}
          spacingProps={spacingProps}
          guidePosition={guidePosition}
          containerWidth={containerWidth}
        />
      );
    },
    [
      items,
      galleryOutline,
      editingItem,
      canEditContent,
      space?.features,
      onOverflowSelected,
      onItemDoubleClicked,
      onItemClicked,
      onUseButtonSelected,
      handleReorderDragEnd,
      handleReorderDragStart,
      _onStartEditing,
      _onFinishEditing,
      containerWidth,
    ]
  );

  if (!items) return null;

  return (
    <Box position="relative" data-inspector-clear="true">
      {renderActionsMenu()}
      <AssetExportController context={exportContext} onClose={closeExportController} />
      {renderInsertAssetMenu()}
      {renderEmptySection()}
      <Box ref={itemWrapperRef} data-clarity-mask="true">
        <GalleryItemsLayout
          items={items}
          canEditContent={canEditContent}
          renderItem={renderItemForGrid}
          onDrop={handleGridDrop}
        />
      </Box>
    </Box>
  );
};

export default GalleryItems;
